"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { config } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, ChevronDown, Menu, Anchor, KeyRound } from "lucide-react";

// Enlaces sueltos antes del menú "Combustible". Entidades núcleo juntas
// (reservas, barcos, cuentas) y luego la vista de meteo.
const NAV_PRE = [
  { href: "/", key: "inicio" },
  { href: "/reservas", key: "reservas" },
  { href: "/barcos", key: "barcos" },
  { href: "/cuentas", key: "cuentas" },
  { href: "/meteo", key: "meteo" },
] as const;

// Menú desplegable "Combustible": informes de consumo + recibos.
const NAV_COMBUSTIBLE = [
  { href: "/informes", key: "informes" },
  { href: "/facturacion", key: "facturacion" },
] as const;
const COMBUSTIBLE_PREFIXES = NAV_COMBUSTIBLE.map((i) => i.href);

// Rutas que viven dentro del desplegable "Ajustes" (para resaltarlo cuando estás en ellas).
const AJUSTES_PREFIXES = ["/ajustes", "/dispositivos"];

// Solo admin: gestión de contenido (alta/edición de artículos).
const NAV_ADMIN = [{ href: "/contenido", key: "contenido" }] as const;

// Rutas del desplegable "Ajustes" (versión plana, para el menú móvil hamburguesa).
const AJUSTES_ITEMS = [
  { href: "/dispositivos", key: "dispositivos" },
  { href: "/ajustes/combustible", key: "ajustesCombustible" },
  { href: "/ajustes/facturacion", key: "ajustesFacturacion" },
  { href: "/ajustes/puntos", key: "ajustesPuntos" },
  { href: "/ajustes/localizaciones", key: "ajustesLocalizaciones" },
  { href: "/ajustes/geofences", key: "ajustesGeofences" },
  { href: "/ajustes/meteo", key: "ajustesMeteo" },
  { href: "/ajustes/notificaciones", key: "ajustesNotificaciones" },
  { href: "/ajustes/retencion", key: "ajustesRetencion" },
] as const;

export function Navbar() {
  const t = useTranslations("nav");
  const { user, loading, logout, changePassword } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [pwOpen, setPwOpen] = useState(false);
  const initials = user ? user.username.slice(0, 2).toUpperCase() : "";
  const isAdmin = user?.roles.includes("admin") ?? false;
  const postNav = isAdmin ? NAV_ADMIN : [];
  // Admin de arranque con clave "de fábrica": se le obliga a cambiarla antes de usar el panel.
  const forced = user?.debeCambiarPassword ?? false;

  const renderLink = (item: { href: string; key: string }) => {
    const active = item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
    return (
      <Link
        key={item.href}
        href={item.href}
        className={
          "rounded-md px-2.5 py-1.5 text-sm transition-colors " +
          (active
            ? "bg-accent text-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-accent/60")
        }
      >
        {t(item.key)}
      </Link>
    );
  };

  // Las pantallas /app/* son la vista "desnuda" del WebView móvil: sin navbar.
  if (pathname.startsWith("/app")) return null;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <Anchor className="h-5 w-5 text-primary" />
            <span className="hidden sm:inline">{config.appName}</span>
          </Link>
          {/* Menú móvil (hamburguesa): toda la navegación en un desplegable cuando no cabe en fila. */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex cursor-pointer items-center rounded-md p-2 text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground md:hidden">
                <Menu className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-[80vh] w-56 overflow-y-auto">
                {NAV_PRE.map((item) => (
                  <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                    {t(item.key)}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {NAV_COMBUSTIBLE.map((item) => (
                  <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                    {t(item.key)}
                  </DropdownMenuItem>
                ))}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    {NAV_ADMIN.map((item) => (
                      <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                        {t(item.key)}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                {AJUSTES_ITEMS.map((item) => (
                  <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                    {t(item.key)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {user && (
            <nav className="hidden items-center gap-1 md:flex">
              {NAV_PRE.map(renderLink)}

              {/* Menú Combustible: informes de consumo + recibos */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={
                    "inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-sm outline-none transition-colors " +
                    (COMBUSTIBLE_PREFIXES.some((p) => pathname.startsWith(p))
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/60")
                  }
                >
                  {t("combustible")}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {NAV_COMBUSTIBLE.map((item) => (
                    <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                      {t(item.key)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {postNav.map(renderLink)}

              <DropdownMenu>
                <DropdownMenuTrigger
                  className={
                    "inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-sm outline-none transition-colors " +
                    (AJUSTES_PREFIXES.some((p) => pathname.startsWith(p))
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/60")
                  }
                >
                  {t("ajustes")}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {/* Hardware / inventario */}
                  <DropdownMenuItem onClick={() => router.push("/dispositivos")}>
                    {t("dispositivos")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Negocio: combustible, recibos y membresía */}
                  <DropdownMenuItem onClick={() => router.push("/ajustes/combustible")}>
                    {t("ajustesCombustible")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/ajustes/facturacion")}>
                    {t("ajustesFacturacion")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/ajustes/puntos")}>
                    {t("ajustesPuntos")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Puertos y condiciones (la meteo es por-puerto) */}
                  <DropdownMenuItem onClick={() => router.push("/ajustes/localizaciones")}>
                    {t("ajustesLocalizaciones")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/ajustes/geofences")}>
                    {t("ajustesGeofences")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/ajustes/meteo")}>
                    {t("ajustesMeteo")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Sistema: comunicaciones y datos */}
                  <DropdownMenuItem onClick={() => router.push("/ajustes/notificaciones")}>
                    {t("ajustesNotificaciones")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/ajustes/retencion")}>
                    {t("ajustesRetencion")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2">
          {loading ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring/50 data-[popup-open]:bg-accent">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium sm:inline">{user.username}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="font-medium">{user.username}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {user.email || "—"}
                    </span>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="mb-1.5 text-xs text-muted-foreground">{t("roles")}</p>
                  <div className="flex flex-wrap gap-1">
                    {user.roles.length ? (
                      user.roles.map((r) => (
                        <span
                          key={r}
                          className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
                        >
                          {r}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">{t("sinRol")}</span>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t("cambiarPassword")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    // Cerrar sesión y volver al login. Navegación DURA (no router.push): además de
                    // borrar la cookie, descarta el estado en memoria de la sesión anterior; sin esto
                    // la vista se quedaba en la última pantalla (nada redirige al poner user=null).
                    await logout();
                    window.location.assign("/login");
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("cerrarSesion")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => router.push("/login")}>{t("iniciarSesion")}</Button>
          )}
        </div>
      </div>

      <ChangePasswordDialog
        open={pwOpen || forced}
        forced={forced}
        onOpenChange={setPwOpen}
        changePassword={changePassword}
      />
    </header>
  );
}

function ChangePasswordDialog({
  open,
  forced,
  onOpenChange,
  changePassword,
}: {
  open: boolean;
  forced: boolean;
  onOpenChange: (o: boolean) => void;
  changePassword: (current: string, next: string) => Promise<void>;
}) {
  const t = useTranslations("nav");
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Cada apertura empieza limpia (Base UI conserva el árbol al cerrar). En modo forzado (admin de
  // arranque) NO se puede cerrar: ignoramos los intentos de cierre hasta que cambie la contraseña.
  const cambiarOpen = (o: boolean) => {
    if (!o && forced) return;
    if (!o) {
      setActual("");
      setNueva("");
      setRepetir("");
      setError(null);
      setOk(false);
      setBusy(false);
    }
    onOpenChange(o);
  };

  const guardar = async () => {
    setError(null);
    if (nueva.length < 8) return setError(t("passwordMin"));
    if (nueva !== repetir) return setError(t("passwordNoCoincide"));
    setBusy(true);
    try {
      await changePassword(actual, nueva);
      setOk(true);
      setActual("");
      setNueva("");
      setRepetir("");
    } catch (e) {
      setError(e instanceof ApiError ? e.body?.message ?? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={cambiarOpen}>
      <DialogContent showCloseButton={!forced}>
        <DialogHeader>
          <DialogTitle>{t("cambiarPasswordTitulo")}</DialogTitle>
          {forced && (
            <DialogDescription>{t("cambiarPasswordForzado")}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pw-actual">{t("passwordActual")}</Label>
            <Input
              id="pw-actual"
              type="password"
              autoComplete="current-password"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw-nueva">{t("passwordNueva")}</Label>
            <Input
              id="pw-nueva"
              type="password"
              autoComplete="new-password"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw-repetir">{t("passwordRepetir")}</Label>
            <Input
              id="pw-repetir"
              type="password"
              autoComplete="new-password"
              value={repetir}
              onChange={(e) => setRepetir(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {ok && <p className="text-sm text-emerald-600">{t("passwordCambiada")}</p>}
        </div>
        <DialogFooter>
          {!forced && (
            <Button variant="ghost" onClick={() => cambiarOpen(false)}>
              {t("cancelar")}
            </Button>
          )}
          <Button onClick={guardar} disabled={busy}>
            {busy ? t("guardando") : t("guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
